import type { Filter } from "@/api/admin/collections/types";
import {
  COLLECTION_FILTER_LABEL,
  COLLECTION_GROUP_BY_LABEL,
  CollectionFilterIdEnum,
  CollectionGroupByEnum,
  type OptionItem,
} from "@/api/admin/types";

import type { FieldResolver } from "./getChanges";

const EMPTY_DISPLAY = "—";

const COLLECTION_TYPE_HISTORY_LABEL: Record<string, string> = {
  RESTUARANTS_LIST: "list",
  SELECTABLE_GROUP: "group",
  REELS: "reels",
};

export type CollectionHistoryOptions = {
  ambiance?: OptionItem[];
  cuisines?: OptionItem[];
  moment?: OptionItem[];
};

const isCollectionFilterId = (value: string): value is CollectionFilterIdEnum =>
  Object.values(CollectionFilterIdEnum).includes(
    value as CollectionFilterIdEnum
  );

const extractFilters = (value: unknown): Filter[] => {
  if (!value || typeof value !== "object") {
    return [];
  }

  if (Array.isArray(value)) {
    return value as Filter[];
  }

  const nested = (value as { filters?: unknown }).filters;

  if (Array.isArray(nested)) {
    return nested as Filter[];
  }

  return [];
};

const resolvePropertyFilterValue = (
  filter: Filter,
  options: CollectionHistoryOptions
): string => {
  const group = filter.group ?? filter.id.split(".")[1];
  const optionsByGroup: Record<string, OptionItem[] | undefined> = {
    ambiance: options.ambiance,
    cuisine: options.cuisines,
    moment: options.moment,
  };

  const match = optionsByGroup[group ?? ""]?.find(
    item => item.id === filter.value
  );

  return match?.name ?? filter.value;
};

const formatFilterValue = (
  filter: Filter,
  options: CollectionHistoryOptions
): string => {
  if (filter.type === "property" || filter.id.startsWith("property.")) {
    return resolvePropertyFilterValue(filter, options);
  }

  return filter.value;
};

export const formatCollectionFilters = (
  value: unknown,
  options: CollectionHistoryOptions = {}
): string | null => {
  const filters = extractFilters(value);

  if (filters.length === 0) {
    return EMPTY_DISPLAY;
  }

  return filters
    .map(filter => {
      const label = isCollectionFilterId(filter.id)
        ? COLLECTION_FILTER_LABEL[filter.id]
        : filter.id;

      return `${label}: ${formatFilterValue(filter, options)}`;
    })
    .join(", ");
};

export const formatCollectionType = (value: unknown): string | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value !== "string") {
    return String(value);
  }

  return COLLECTION_TYPE_HISTORY_LABEL[value] ?? value;
};

export const formatCollectionGroupBy = (value: unknown): string | null => {
  if (value === null || value === undefined || value === "") {
    return EMPTY_DISPLAY;
  }

  if (typeof value !== "string") {
    return String(value);
  }

  return COLLECTION_GROUP_BY_LABEL[value as CollectionGroupByEnum] ?? value;
};

export const formatStringListValue = (value: unknown): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  if (!Array.isArray(value)) {
    if (typeof value === "string") {
      return value;
    }

    return String(value);
  }

  if (value.length === 0) {
    return EMPTY_DISPLAY;
  }

  return value.map(item => String(item)).join(",\n");
};

export const createCollectionHistoryResolvers = (
  options: CollectionHistoryOptions = {}
): Record<string, FieldResolver> => ({
  type: {
    formatValue: formatCollectionType,
  },
  groupBy: {
    formatValue: formatCollectionGroupBy,
  },
  brandNames: {
    formatValue: formatStringListValue,
  },
  filters: {
    getValue: snapshot => snapshot.filters,
    formatValue: value => formatCollectionFilters(value, options),
  },
});
